import axios from 'axios';

// Use relative URLs in production (Netlify), absolute for local dev
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// low-level fetch to backend (note `/api/images` to match backend)
export async function fetchImages(tag = '', page = 1, limit = 8, seed?: number) {
  const params: any = { tag, page, limit };
  if (seed !== undefined) params.seed = seed;
  const response = await axios.get(`${API_BASE_URL}/api/images`, { params });
  // normalize
  const items = response.data.items ?? response.data.images ?? response.data;
  // map filenames to proxy URLs
  const mapped = (items || []).map((it: any) => ({
    ...it,
    url: `${API_BASE_URL}/api/images/${encodeURIComponent(it.filename)}/image`
  }));
  return { images: mapped, page: response.data.page ?? page, limit: response.data.limit ?? limit, total: response.data.total ?? mapped.length };
}

// used by Gallery: returns { images, hasMore, page, limit, total }
export async function fetchImagesByTag(tag = '', page = 1, limit = 8, seed?: number) {
  const data = await fetchImages(tag, page, limit, seed);

  // normalize different possible backend shapes (avoid `??` and inline `as` which your parser chokes on)
  const anyData: any = data;
  const images = anyData.images || anyData.items || [];
  const total = anyData.total || anyData.totalItems || 0;
  const pageNum = anyData.page || page;
  const limitNum = anyData.limit || limit;
  const hasMore = total > pageNum * limitNum;

  return { images, hasMore, page: pageNum, limit: limitNum, total };
}

// convenience alias used to load additional pages
export async function fetchMoreImages(tag = '', page = 1, limit = 8, seed?: number) {
  return fetchImagesByTag(tag, page, limit, seed);
}

export async function fetchImageById(id: string) {
  const response = await axios.get(`${API_BASE_URL}/api/images/${encodeURIComponent(id)}`);
  return response.data;
}

export async function fetchTotalCount(): Promise<{ total: number; tagged: number }> {
  const response = await axios.get(`${API_BASE_URL}/api/images/count`);
  return { total: response.data.total, tagged: response.data.tagged };
}

/**
 * Fetches a signed GCS URL for an image that is publicly accessible.
 * This URL can be used by external services (like Zazzle) to access the image.
 * The signed URL expires after 1 hour.
 */
export async function fetchSignedImageUrl(filename: string): Promise<string> {
  const response = await axios.get(`${API_BASE_URL}/api/images/${encodeURIComponent(filename)}/url`);
  return response.data.url;
}